import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  useTheme,
  Checkbox,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ProfilNutzerprofilSection from './ProfilNutzerprofilSection';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/NotificationContext';
import { authService } from '../services/authService';
import { validatePasswordPolicy } from '../utils/passwordPolicy';
import {
  benachrichtigungenService,
  BenachrichtigungenResponse,
  EventDefinition,
} from '../services/benachrichtigungenService';

const ProfilPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;
  const { user, refreshUser } = useAuth();
  const { showSuccess } = useNotification();
  const navigate = useNavigate();

  // 2FA setup dialog
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [qrCodeBase64, setQrCodeBase64] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  // 2FA disable
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const passwordPolicy = useMemo(() => validatePasswordPolicy(newPassword), [newPassword]);

  // Restricted-Passwort (eingeschraenkter Zugang fuer AI-Tools)
  const [restrictedCurrent, setRestrictedCurrent] = useState('');
  const [restrictedNew, setRestrictedNew] = useState('');
  const [restrictedConfirm, setRestrictedConfirm] = useState('');
  const [restrictedLoading, setRestrictedLoading] = useState(false);
  const [restrictedError, setRestrictedError] = useState('');
  const [restrictedRemoveOpen, setRestrictedRemoveOpen] = useState(false);
  const restrictedPolicy = useMemo(() => validatePasswordPolicy(restrictedNew), [restrictedNew]);

  // Feedback
  const [error, setError] = useState<string | null>(null);

  // Benachrichtigungen
  const [events, setEvents] = useState<EventDefinition[]>([]);
  const [prefs, setPrefs] = useState<BenachrichtigungenResponse | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [selectedEventsEmail, setSelectedEventsEmail] = useState<string[]>([]);
  const [selectedEventsGotify, setSelectedEventsGotify] = useState<string[]>([]);
  const [selectedEventsInApp, setSelectedEventsInApp] = useState<string[]>([]);
  const [schwachstellenNurBetroffene, setSchwachstellenNurBetroffene] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setPrefsLoading(true);
        const [eventsData, prefsData] = await Promise.all([
          benachrichtigungenService.listEvents({ filter_by_role: true, only_implemented: true }),
          benachrichtigungenService.getOwn(),
        ]);
        setEvents(eventsData);
        setPrefs(prefsData);
        setSelectedEventsEmail(prefsData.events_email || []);
        setSelectedEventsGotify(prefsData.events_gotify || []);
        setSelectedEventsInApp(prefsData.events_inapp || []);
        setSchwachstellenNurBetroffene(Boolean(prefsData.schwachstellen_nur_betroffene));
      } catch {
        setPrefs(null);
      } finally {
        setPrefsLoading(false);
      }
    };
    load();
  }, []);

  const handleToggleEventEmail = (eventId: string) => {
    setSelectedEventsEmail((prev) =>
      prev.includes(eventId)
        ? prev.filter((e) => e !== eventId)
        : [...prev, eventId]
    );
  };

  const handleToggleEventGotify = (eventId: string) => {
    setSelectedEventsGotify((prev) =>
      prev.includes(eventId)
        ? prev.filter((e) => e !== eventId)
        : [...prev, eventId]
    );
  };

  const handleToggleEventInApp = (eventId: string) => {
    setSelectedEventsInApp((prev) =>
      prev.includes(eventId)
        ? prev.filter((e) => e !== eventId)
        : [...prev, eventId]
    );
  };

  const handleSaveBenachrichtigungen = async () => {
    setPrefsSaving(true);
    try {
      const updated = await benachrichtigungenService.updateOwn({
        events_email: selectedEventsEmail,
        events_gotify: selectedEventsGotify,
        events_inapp: selectedEventsInApp,
        schwachstellen_nur_betroffene: schwachstellenNurBetroffene,
      });
      setPrefs(updated);
      setSchwachstellenNurBetroffene(Boolean(updated.schwachstellen_nur_betroffene));
      showSuccess('Benachrichtigungs-Einstellungen gespeichert');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleSetup2FA = async () => {
    setSetupLoading(true);
    setVerifyError('');
    setVerifyCode('');
    try {
      const data = await authService.setup2FA();
      setQrCodeBase64(data.qr_code_base64);
      setTotpSecret(data.secret);
      setSetupDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Einrichten der 2FA');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setVerifyError('Bitte einen 6-stelligen Code eingeben');
      return;
    }
    setVerifyLoading(true);
    setVerifyError('');
    try {
      await authService.verify2FA(totpSecret, verifyCode);
      setSetupDialogOpen(false);
      showSuccess('2FA erfolgreich aktiviert');
      setQrCodeBase64('');
      setTotpSecret('');
      setVerifyCode('');
      await refreshUser();
    } catch (err: any) {
      setVerifyError(err.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setDisableLoading(true);
    try {
      await authService.disable2FA();
      setDisableDialogOpen(false);
      showSuccess('2FA erfolgreich deaktiviert');
      await refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Deaktivieren der 2FA');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!passwordPolicy.valid) {
      setPasswordError(`Passwort-Richtlinie verletzt: ${passwordPolicy.errors.join('; ')}`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwörter stimmen nicht überein');
      return;
    }
    setPasswordLoading(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      showSuccess('Passwort wurde erfolgreich geändert');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSetRestrictedPassword = async () => {
    setRestrictedError('');
    if (!restrictedPolicy.valid) {
      setRestrictedError(`Passwort-Richtlinie verletzt: ${restrictedPolicy.errors.join('; ')}`);
      return;
    }
    if (restrictedNew !== restrictedConfirm) {
      setRestrictedError('Passwörter stimmen nicht überein');
      return;
    }
    setRestrictedLoading(true);
    try {
      await authService.setRestrictedPassword(restrictedCurrent, restrictedNew);
      showSuccess('Restricted-Passwort wurde gesetzt');
      setRestrictedCurrent('');
      setRestrictedNew('');
      setRestrictedConfirm('');
      await refreshUser();
    } catch (err: any) {
      setRestrictedError(err.response?.data?.detail || 'Fehler beim Setzen des Restricted-Passworts');
    } finally {
      setRestrictedLoading(false);
    }
  };

  const handleRemoveRestrictedPassword = async () => {
    setRestrictedLoading(true);
    try {
      await authService.removeRestrictedPassword();
      showSuccess('Eingeschränkter Zugang wurde deaktiviert');
      setRestrictedRemoveOpen(false);
      await refreshUser();
    } catch (err: any) {
      setRestrictedError(err.response?.data?.detail || 'Fehler beim Deaktivieren');
    } finally {
      setRestrictedLoading(false);
    }
  };

  const handleCloseSetupDialog = () => {
    setSetupDialogOpen(false);
    setQrCodeBase64('');
    setTotpSecret('');
    setVerifyCode('');
    setVerifyError('');
  };

  if (!user) return null;

  return (
    <Box>
      <PageHeader
        title="Mein Profil"
        breadcrumbs={[{ label: 'Profil' }]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}


      {/* Benutzerinfos */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            Benutzerinformationen
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                Vorname
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {user.vorname}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                Nachname
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {user.nachname}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                E-Mail
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {user.email}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                Rollen
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {user.rollen?.map(r => r.name).join(', ') || 'Keine Rolle'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <ProfilNutzerprofilSection user={user} accent={accent} onUpdated={refreshUser} />

      {/* Passwort aendern */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <LockIcon sx={{ color: accent }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Passwort ändern
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Ändern Sie Ihr Passwort regelmäßig, um die Sicherheit Ihres Kontos zu gewährleisten.
          </Typography>

          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>
              {passwordError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
            <TextField
              label="Aktuelles Passwort"
              type="password"
              size="small"
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
            <TextField
              label="Neues Passwort"
              type="password"
              size="small"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              helperText="20+ Zeichen, Gross/Klein, Ziffer, Sonderzeichen, keine simplen Muster"
            />
            {newPassword && (
              <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1.5 }}>
                {passwordPolicy.checks.map((check) => (
                  <Typography
                    key={check.key}
                    variant="caption"
                    sx={{ display: 'block', color: check.passed ? 'success.main' : 'error.main' }}
                  >
                    {check.passed ? 'OK ' : 'X '} {check.label}
                  </Typography>
                ))}
              </Box>
            )}
            <TextField
              label="Neues Passwort bestätigen"
              type="password"
              size="small"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
            <Box>
              <Button
                variant="contained"
                onClick={handleChangePassword}
                disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                startIcon={passwordLoading ? <CircularProgress size={18} /> : <LockIcon />}
              >
                Passwort ändern
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Eingeschraenkter Zugang (AI-Tools) */}
      <Card sx={{ mb: 3 }} data-testid="restricted-access-card">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <SmartToyIcon sx={{ color: accent }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Eingeschränkter Zugang für AI-Tools
            </Typography>
            {user.has_restricted_password ? (
              <Chip icon={<CheckCircleIcon />} label="Aktiv" color="success" size="small" variant="outlined" />
            ) : (
              <Chip icon={<CancelIcon />} label="Nicht eingerichtet" size="small" variant="outlined" />
            )}
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Mit einem separaten <strong>Restricted-Passwort</strong> können sich AI-Tools mit Ihrer E-Mail-Adresse anmelden,
            erhalten aber nur eine <strong>eingeschränkte Sitzung</strong>: Sie sehen ausschließlich Ihre isolierte Umgebung
            (eigene Findings anlegen, bearbeiten, archivieren sowie von Ihnen freigegebene Audits/Findings lesen).
            Alle anderen Daten des Systems bleiben unzugänglich.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Für den Restricted-Login wird kein 2FA-Code benötigt. Es gilt dieselbe Passwort-Richtlinie wie für Ihr
            Hauptpasswort; das Restricted-Passwort darf nicht mit dem Hauptpasswort identisch sein.
            {' '}
            <Button size="small" onClick={() => navigate('/isolierte-umgebung')} sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}>
              Zur isolierten Umgebung
            </Button>
          </Typography>

          {restrictedError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRestrictedError('')}>
              {restrictedError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
            <TextField
              label="Hauptpasswort (Bestätigung)"
              type="password"
              size="small"
              fullWidth
              value={restrictedCurrent}
              onChange={(e) => setRestrictedCurrent(e.target.value)}
              autoComplete="current-password"
              data-testid="restricted-current"
            />
            <TextField
              label={user.has_restricted_password ? 'Neues Restricted-Passwort' : 'Restricted-Passwort'}
              type="password"
              size="small"
              fullWidth
              value={restrictedNew}
              onChange={(e) => setRestrictedNew(e.target.value)}
              autoComplete="new-password"
              helperText="20+ Zeichen, Gross/Klein, Ziffer, Sonderzeichen, keine simplen Muster"
              data-testid="restricted-new"
            />
            {restrictedNew && (
              <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1.5 }}>
                {restrictedPolicy.checks.map((check) => (
                  <Typography
                    key={check.key}
                    variant="caption"
                    sx={{ display: 'block', color: check.passed ? 'success.main' : 'error.main' }}
                  >
                    {check.passed ? 'OK ' : 'X '} {check.label}
                  </Typography>
                ))}
              </Box>
            )}
            <TextField
              label="Restricted-Passwort bestätigen"
              type="password"
              size="small"
              fullWidth
              value={restrictedConfirm}
              onChange={(e) => setRestrictedConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="restricted-confirm"
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleSetRestrictedPassword}
                disabled={restrictedLoading || !restrictedCurrent || !restrictedNew || !restrictedConfirm}
                startIcon={restrictedLoading ? <CircularProgress size={18} /> : <SmartToyIcon />}
                data-testid="restricted-save"
              >
                {user.has_restricted_password ? 'Restricted-Passwort ändern' : 'Eingeschränkten Zugang einrichten'}
              </Button>
              {user.has_restricted_password && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setRestrictedRemoveOpen(true)}
                  disabled={restrictedLoading}
                >
                  Zugang deaktivieren
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Benachrichtigungen */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <NotificationsActiveIcon sx={{ color: accent }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Meine Benachrichtigungen
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Wählen Sie, welche Ereignisse Sie per E-Mail, Push (Gotify) und/oder In-App erhalten möchten.
            Für Push werden Gotify-Token und Server-Adresse (URL) benötigt. Die Pflege übernimmt der Administrator;
            die Standard-URL kann unter Verwaltung → Systemkonfiguration → Gotify (Push) gesetzt werden.
          </Typography>

          <FormControlLabel
            sx={{ mb: 2, alignItems: 'flex-start', ml: 0 }}
            control={
              <Switch
                checked={schwachstellenNurBetroffene}
                onChange={(_, v) => setSchwachstellenNurBetroffene(v)}
                disabled={prefsLoading || prefs?.admin_override_active}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Schwachstellen: nur direkt betreffende Ereignisse
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', maxWidth: 560 }}>
                  Wenn aktiv, erhalten Sie bei ausgewählten Schwachstellen-Meldungen (z.&nbsp;B. Geprüft,
                  Zurückgewiesen, Kommentar) nur Benachrichtigungen, bei denen Sie Reporter der Schwachstelle sind – nicht
                  als reines Audit-Team-Mitglied bei den fremden Findings anderer. Genehmigungs- und Anlege-Aktionen
                  lösen für Sie selbst keine Meldung aus.
                </Typography>
              </Box>
            }
          />

          {prefsLoading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {prefs?.gotify_configured ? (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: accent, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Gotify: Aktiv (Token und Server-Adresse sind hinterlegt; Push-Zustellung möglich)
                  </Typography>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Gotify ist noch nicht vollständig eingerichtet. Es werden ein Token und die Gotify-Server-URL
                  benötigt (URL pro Person, global in der Systemkonfiguration oder Umgebungsvariable GOTIFY_URL).
                </Alert>
              )}

              {prefs?.admin_override_active ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Der Administrator hat Ihre Event-Auswahl überschrieben. Die folgenden Einstellungen gelten:
                  </Alert>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Aktive Ereignisse (vom Admin festgelegt) – E-Mail: {(prefs?.effective_events_email || []).length} / Gotify: {(prefs?.effective_events_gotify || []).length} / In-App: {(prefs?.effective_events_inapp || []).length} von {events.length}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Ereignis</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>E-Mail</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Gotify</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>In-App</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(
                        events.reduce<Record<string, EventDefinition[]>>((acc, ev) => {
                          if (!acc[ev.category]) acc[ev.category] = [];
                          acc[ev.category].push(ev);
                          return acc;
                        }, {})
                      ).flatMap(([category, evs]) => [
                        <TableRow key={`cat-${category}`}>
                          <TableCell colSpan={4} sx={{ pt: 1.5, pb: 0.5, fontWeight: 600, color: 'text.secondary' }}>
                            {category}
                          </TableCell>
                        </TableRow>,
                        ...evs.map((ev) => (
                          <TableRow key={ev.id}>
                            <TableCell sx={{ pl: 3 }}>{ev.label}</TableCell>
                            <TableCell align="center">
                              <Checkbox size="small" checked={(prefs?.effective_events_email || []).includes(ev.id)} disabled />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox size="small" checked={(prefs?.effective_events_gotify || []).includes(ev.id)} disabled />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox size="small" checked={(prefs?.effective_events_inapp || []).includes(ev.id)} disabled />
                            </TableCell>
                          </TableRow>
                        )),
                      ])}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Ereignisse – E-Mail: {selectedEventsEmail.length} / Gotify: {selectedEventsGotify.length} / In-App: {selectedEventsInApp.length} aktiviert
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Ereignis</TableCell>
                        {[
                          { label: 'E-Mail', selected: selectedEventsEmail, setSelected: setSelectedEventsEmail },
                          { label: 'Gotify', selected: selectedEventsGotify, setSelected: setSelectedEventsGotify },
                          { label: 'In-App', selected: selectedEventsInApp, setSelected: setSelectedEventsInApp },
                        ].map(({ label, selected, setSelected }) => (
                          <TableCell key={label} align="center" sx={{ fontWeight: 600, verticalAlign: 'bottom' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{label}</Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setSelected([])}
                                  disabled={selected.length === 0}
                                  sx={{ minWidth: 0, px: 0.8, py: 0, fontSize: '0.65rem', lineHeight: 1.8 }}
                                >
                                  ab
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setSelected(events.map((e) => e.id))}
                                  disabled={selected.length === events.length}
                                  sx={{ minWidth: 0, px: 0.8, py: 0, fontSize: '0.65rem', lineHeight: 1.8 }}
                                >
                                  an
                                </Button>
                              </Box>
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(
                        events.reduce<Record<string, EventDefinition[]>>((acc, ev) => {
                          if (!acc[ev.category]) acc[ev.category] = [];
                          acc[ev.category].push(ev);
                          return acc;
                        }, {})
                      ).flatMap(([category, evs]) => [
                        <TableRow key={`cat-${category}`}>
                          <TableCell colSpan={4} sx={{ pt: 1.5, pb: 0.5, fontWeight: 600, color: 'text.secondary' }}>
                            {category}
                          </TableCell>
                        </TableRow>,
                        ...evs.map((ev) => (
                          <TableRow key={ev.id}>
                            <TableCell sx={{ pl: 3 }}>{ev.label}</TableCell>
                            <TableCell align="center">
                              <Checkbox size="small" checked={selectedEventsEmail.includes(ev.id)} onChange={() => handleToggleEventEmail(ev.id)} />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox size="small" checked={selectedEventsGotify.includes(ev.id)} onChange={() => handleToggleEventGotify(ev.id)} />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox size="small" checked={selectedEventsInApp.includes(ev.id)} onChange={() => handleToggleEventInApp(ev.id)} />
                            </TableCell>
                          </TableRow>
                        )),
                      ])}
                    </TableBody>
                  </Table>
                  <Button
                    variant="contained"
                    onClick={handleSaveBenachrichtigungen}
                    disabled={prefsSaving}
                    startIcon={prefsSaving ? <CircularProgress size={18} /> : null}
                    sx={{ mt: 2 }}
                  >
                    {prefsSaving ? 'Speichern...' : 'Speichern'}
                  </Button>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 2FA-Bereich */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <ShieldIcon sx={{ color: accent }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Zwei-Faktor-Authentifizierung (2FA)
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Schützen Sie Ihr Konto mit einer zusätzlichen Sicherheitsebene.
            Bei aktivierter 2FA wird bei der Anmeldung ein Code aus Ihrer Authenticator-App benötigt.
          </Typography>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderRadius: 2,
            bgcolor: isDark ? '#1a1f3c' : '#f5f7fa',
            border: `1px solid ${theme.palette.divider}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SecurityIcon sx={{ color: user.has_2fa ? accent : theme.palette.text.secondary, fontSize: 32 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {user.has_2fa ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="2FA aktiv"
                      size="small"
                      sx={{
                        bgcolor: isDark ? '#1a3a2a' : '#e8f5e9',
                        color: accent,
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: accent },
                      }}
                    />
                  ) : (
                    <Chip
                      icon={<CancelIcon />}
                      label="2FA inaktiv"
                      size="small"
                      sx={{
                        bgcolor: isDark ? '#3a2020' : '#fbe9e7',
                        color: '#ef5350',
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: '#ef5350' },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {user.has_2fa ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDisableDialogOpen(true)}
                sx={{ flexShrink: 0 }}
              >
                Deaktivieren
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSetup2FA}
                disabled={setupLoading}
                startIcon={setupLoading ? <CircularProgress size={18} /> : <ShieldIcon />}
                sx={{ flexShrink: 0 }}
              >
                Aktivieren
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Setup-Dialog */}
      <Dialog
        open={setupDialogOpen}
        onClose={handleCloseSetupDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          2FA einrichten
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Scannen Sie den QR-Code mit Ihrer Authenticator-App (z.B. Google Authenticator, Authy)
            und geben Sie anschließend den generierten 6-stelligen Code ein.
          </Typography>

          {qrCodeBase64 && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                component="img"
                src={`data:image/png;base64,${qrCodeBase64}`}
                alt="2FA QR-Code"
                sx={{
                  width: 220,
                  height: 220,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  p: 1,
                  bgcolor: '#fff',
                }}
              />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Backup-Schlüssel (sicher aufbewahren):
          </Typography>
          <Box sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: isDark ? '#1a1f3c' : '#f5f7fa',
            border: `1px solid ${theme.palette.divider}`,
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            letterSpacing: '0.1em',
            textAlign: 'center',
            mb: 3,
            wordBreak: 'break-all',
            userSelect: 'all',
          }}>
            {totpSecret}
          </Box>

          {verifyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {verifyError}
            </Alert>
          )}

          <TextField
            label="6-stelliger Code"
            fullWidth
            value={verifyCode}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setVerifyCode(val);
            }}
            placeholder="000000"
            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseSetupDialog}
            sx={{ color: 'text.secondary' }}
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleVerify2FA}
            disabled={verifyLoading || verifyCode.length !== 6}
          >
            {verifyLoading ? <CircularProgress size={22} /> : 'Verifizieren & Aktivieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable-Dialog */}
      <ConfirmDialog
        open={disableDialogOpen}
        title="2FA deaktivieren"
        message="Möchten Sie die Zwei-Faktor-Authentifizierung wirklich deaktivieren? Ihr Konto ist danach weniger geschützt."
        onConfirm={handleDisable2FA}
        onCancel={() => setDisableDialogOpen(false)}
        confirmText={disableLoading ? 'Wird deaktiviert...' : 'Deaktivieren'}
        cancelText="Abbrechen"
      />

      <ConfirmDialog
        open={restrictedRemoveOpen}
        title="Eingeschränkten Zugang deaktivieren"
        message="Das Restricted-Passwort wird entfernt. Laufende eingeschränkte Sitzungen (z.B. von AI-Tools) werden sofort ungültig. Ihre Findings in der isolierten Umgebung bleiben erhalten."
        onConfirm={handleRemoveRestrictedPassword}
        onCancel={() => setRestrictedRemoveOpen(false)}
        confirmText={restrictedLoading ? 'Wird deaktiviert...' : 'Deaktivieren'}
        cancelText="Abbrechen"
      />
    </Box>
  );
};

export default ProfilPage;
